import React, { Fragment, useState, useCallback, useMemo } from 'react';
import _ from 'lodash';

import AddWordSelection from './AddWordSelection';

import helpers from 'helpers';
import { letterMapper, AnnotationTooltip } from 'helpers/annotations';
import { renderUsefulLines, isLineUseful } from './utils';
import { useBatch, BATCH_TYPES } from './BatchContext';

const ContentGazetter = ({
    textLetter,
    gazetters,
    addNewGazetter,
    withChildren,
    predictorData,
    showUsefulLines,
    lineScoreData,
}) => {
    const [openAddWordSelection, setOpenAddWordSelection] = useState(false);
    const [elementTarget, setElementTarget] = useState(null);
    const [selectedText, setSelectedText] = useState('');

    const { addToBatch, removeFromBatch, data: batchData } = useBatch();

    const createCharacters = useCallback((text, gazettersFromServer) => {
        const gazettersCopy = _.cloneDeep(gazettersFromServer);
        let characters = text.split('');

        characters = characters.map((symbol, symbolPosition) => ({
            symbol,
            symbolPosition,
            style: {},
            ids: [],
            tooltipContent: [],
        }));

        gazettersCopy.sort((first, second) => {
            if (withChildren) {
                if (first.end - first.start < second.end - second.start) {
                    return -1;
                }
                if (first.end - first.start > second.end - second.start) {
                    return 1;
                }
                return 0;
            }
            if (first.end - first.start > second.end - second.start) {
                return -1;
            }
            if (first.end - first.start < second.end - second.start) {
                return 1;
            }
            return 0;
        });

        gazettersCopy.forEach((g) => {
            for (let j = g.start; j < g.end; j++) {
                characters[j].style = {
                    background: g.color,
                };
                characters[j].ids.push(g.id);
                characters[j].tooltipContent.push({
                    gazetteerName: g.gazetteerName,
                    gazetteerType: g.gazetteerType,
                });
            }
        });

        return characters;
    }, [withChildren]);

    const createTooltips = useCallback((characters) => {
        const map = new Map();
        characters.forEach((char) => {
            map.set(char.ids.join(''), char);
        });
        return Array.from(map.values());
    }, []);

    const { characters, tooltips } = useMemo(() => {
        if (textLetter && textLetter.length && gazetters && gazetters.length) {
            const chars = createCharacters(textLetter, gazetters);
            return {
                characters: chars,
                tooltips: createTooltips(chars),
            };
        }
        return { characters: undefined, tooltips: undefined };
    }, [textLetter, gazetters, createCharacters, createTooltips]);

    const onMouseUp = useCallback((ev) => {
        const range = helpers.getSelection(ev.target.parentNode);

        if (range.start === range.end) {
            return;
        }

        setOpenAddWordSelection(true);
        setElementTarget(ev.target);
        setSelectedText(range.string);
    }, []);

    const closePopover = useCallback(() => {
        setOpenAddWordSelection(false);
    }, []);

    const onLineClick = useCallback((ev, line, matchedData) => {
        if (!matchedData) return;

        const trimmedLine = line.trim();
        
        const originalIsUseful = isLineUseful(matchedData);

        const batchType = originalIsUseful 
            ? BATCH_TYPES.USEFUL_TO_NOT_USEFUL 
            : BATCH_TYPES.NOT_USEFUL_TO_USEFUL;

        const batchArray = originalIsUseful 
            ? batchData.usefulToNotUseful 
            : batchData.notUsefulToUseful;
        const normalizedLine = trimmedLine.toLowerCase();
        const existingItem = batchArray.find(item => item.line.trim().toLowerCase() === normalizedLine);

        if (existingItem) {
            removeFromBatch(existingItem.id, batchType);
        } else {
            addToBatch(line, batchType);
        }
    }, [addToBatch, removeFromBatch, batchData]);

    return (
        <Fragment>
            <AddWordSelection
                addNewGazetter={addNewGazetter}
                openAddWordSelection={openAddWordSelection}
                elementTarget={elementTarget}
                closePopover={closePopover}
                selectedText={selectedText}
            />
            <div className='letter-text' onMouseUp={onMouseUp}>
                {
                    (() => {
                        if (showUsefulLines) {
                            return renderUsefulLines(
                                predictorData, 
                                textLetter, 
                                onLineClick, 
                                lineScoreData,
                                batchData,
                            );
                        }
                        if (characters) {
                            return Array.from(letterMapper(characters));
                        }
                        return textLetter;
                    })()
                }
            </div>
            <AnnotationTooltip
                tooltips={tooltips}
                renderTooltip={tooltip => (
                    <div className='tooltip-row'>
                        {
                            tooltip.tooltipContent.map((t, k) => (
                                <div key={k} className='tooltip-column'>
                                    <div className='col'>{`GazetteerName - ${t.gazetteerName}, `}</div>
                                    <div className='col'>{`GazetteerType - ${t.gazetteerType}`}</div>
                                </div>
                            ))
                        }
                    </div>
                )}
            />
        </Fragment>
    );
};

export default ContentGazetter;



// WEBPACK FOOTER //
// ./app/components/Letters/Single/Gazetter/Content/Gazetters.jsx